import { Component, OnInit, signal, WritableSignal } from "@angular/core"
import { ActivatedRoute, Router } from "@angular/router";
import { MessageService } from "primeng/api"
import { RoleServicesService } from "../../services/role-services.service"
import { GlobalServicesService } from "src/app/services/global-services.service"
import { BreadcrumbService } from "xng-breadcrumb"
import { FormGroup, FormControl, Validators, FormArray } from "@angular/forms";
import { CountryStateCityService } from "src/app/services/country-state-city.service";
import { SharedServicesService } from "src/app/services/shared-services.service";
import { FileUploadService } from "src/app/services/file-upload.service";
import { environment } from "src/environments/environment";
import { InnerHtmlViewComponent } from "src/app/shared/innerhtml.component";
import { GetSafeHTMLEmailPipe } from "src/app/pipes/getSafeHTMLEmail.pipe";

declare let tinymce: any;

@Component({
    selector: "app-job-details",
    templateUrl: "./job-details.component.html",
    styleUrls: ["./job-details.component.css"],
    providers: [MessageService],
    standalone: false
})
export class JobDetailsComponent implements OnInit {
  roleId = ""
  roleData: any = null
  loadingSpinner = false
  userPrivileges: Set<string> = new Set()

  // Form controls
  roleDetailsForm!: FormGroup;
  locationInformation!: FormArray;

  // Editing states
  editingDescription: WritableSignal<boolean> = signal<boolean>(false);

  // Dropdown data
  roleTypes: any[] = [];
  workModes: any[] = [
    { name: 'Remote', value: 'Remote' },
    { name: 'Hybrid', value: 'Hybrid' },
    { name: 'In Office', value: 'Office' }
  ];
  countries: any[] = [];
  locationOptions: any[] = [];
  jsonData: any = null;
  jsonDataLoaded: Promise<void> | null = null;

  // Selected values for tracking
  selectedRoleType: any = null;
  selectedWorkMode: any = null;

  // TinyMCE
  tinymce_api_key = environment.HOST.TINYMCE_API_KEY;
  showEditor = false;

  // Validation
  clicked = false;
  saveBtnLoading = false;

  // Change tracking
  hasChanges = false;

  // File upload
  mediaFile: File[] = [];
  attachmentName: string | null = null;
  uploadingFile = false;
  companyId: string = '';

  constructor(
    private activateRoute: ActivatedRoute,
    private router: Router,
    private roleService: RoleServicesService,
    private messageService: MessageService,
    public globalService: GlobalServicesService,
    private breadcrumbService: BreadcrumbService,
    private countryStateCityService: CountryStateCityService,
    private sharedService: SharedServicesService,
    private fileUploadService: FileUploadService
  ) { }

  ngOnInit(): void {
    this.companyId = localStorage.getItem('company-details') ? JSON.parse(localStorage.getItem('company-details') || '{}').company_settings?.school_code : '';

    this.globalService.privilegeDetailsSubject.subscribe((privileges) => {
      this.userPrivileges = privileges;
      if ((this.userPrivileges.has('job_role.update') || this.userPrivileges.has('job_role.create'))) {
        this.loadRoleTypes();
      }
    });

    this.initializeForm();
    this.loadCountryStateCityData();

    this.activateRoute.params.subscribe(params => {
      this.roleId = params['roleId'];
      if (this.roleId) {
        this.getRoleDetails();
      }
    })

    // Track form changes
    this.roleDetailsForm.valueChanges.subscribe(() => {
      this.hasChanges = true;
    });


  }

  initializeForm(): void {
    this.roleDetailsForm = new FormGroup({
      roleName: new FormControl('', Validators.required),
      roleType: new FormControl('', Validators.required),
      workMode: new FormControl('', Validators.required),
      roleDescription: new FormControl('', [Validators.required, Validators.minLength(250)]),
      locationInformation: new FormArray([])
    });

    this.locationInformation = this.roleDetailsForm.get('locationInformation') as FormArray;
  }

  getRoleDetails(): void {
    this.loadingSpinner = true

    this.roleService.getSpecificJobRolesFastApi(this.roleId).subscribe({
      next: (response: any) => {
        if (response.status === 200 && response.body) {
          this.roleData = response.body
          this.populateForm();
          this.updateBreadcrumb()
          if (this.roleData.attachment) {
            this.attachmentName = this.roleData.attachment.split('/').pop();
          }
        }
        this.loadingSpinner = false
      },
      error: () => {
        this.loadingSpinner = false
        this.messageService.add({
          severity: "error",
          summary: "Error",
          detail: "Failed to load role details",
        })
      },
    })
  }

  async populateForm(): Promise<void> {
    if (!this.roleData) return;

    this.roleDetailsForm.patchValue({
      roleName: this.roleData.name || '',
      roleType: this.roleData.role_type?.id || '',
      workMode: this.roleData.work_mode || '',
      roleDescription: this.roleData.description || ''
    });

    this.selectedRoleType = this.roleData.role_type?.id || null;
    this.selectedWorkMode = this.roleData.work_mode || null;
    if (this.jsonDataLoaded) await this.jsonDataLoaded;

    // Populate locations
    this.locationInformation.clear();
    this.locationOptions = [];
    if (this.roleData.locations && this.roleData.locations.length > 0) {
      this.roleData.locations.forEach((location: any, index: number) => {
        this.addLocationItem(location);
        if (location.country && this.jsonData[location.country]) {
          this.locationOptions[index].states = Object.keys(this.jsonData[location.country]).map(state => ({
            label: state,
            value: state
          }));
          if (location.state && this.jsonData[location.country][location.state]) {
            this.locationOptions[index].cities = this.jsonData[location.country][location.state].map((city: string) => ({
              label: city,
              value: city
            }));
          }
        }
      });
    } else {
      // Add one empty location if no locations exist
      this.addLocationItem();
    }

    // Reset hasChanges after initial population
    setTimeout(() => {
      this.hasChanges = false;
    }, 500);
  }

  addLocationItem(location?: any): void {
    const cityValue = location?.city
      ? (Array.isArray(location.city) && location.city.length > 0 ? location.city[0] : location.city)
      : '';

    this.locationOptions.push({ states: [], cities: [] });

    const locationGroup = new FormGroup({
      country: new FormControl(location?.country || '', Validators.required),
      state: new FormControl(location?.state || '', Validators.required),
      city: new FormControl(cityValue, Validators.required),
      businessUnit: new FormControl(location?.business_unit || '', Validators.required),
      openings: new FormControl(location?.opening || 0),
      maximumApplicants: new FormControl(location?.max_applicant || 0),
      diversityMale: new FormControl(location?.diversity_male || 0),
      diversityFemale: new FormControl(location?.diversity_female || 0),
      diversityOther: new FormControl(location?.diversity_other || 0)
    });

    this.locationInformation.push(locationGroup);
  }

  removeLocationItem(index: number): void {
    this.locationInformation.removeAt(index);
    this.locationOptions.splice(index, 1);
  }

  loadRoleTypes(): void {
    this.roleService.getJobRolesFormDataFastApi().subscribe({
      next: (response: any) => {
        if (response.status === 200 && response.body) {
          this.roleTypes = response.body.role_types.map((element: any) => ({
            name: element.name,
            id: element.id
          }));
        }
      },
      error: () => {
        this.messageService.add({
          severity: "error",
          summary: "Error",
          detail: "Failed to load role types",
        })
      }
    });
  }

  loadCountryStateCityData(): void {
    this.jsonDataLoaded = new Promise<void>((resolve) => {
      this.countryStateCityService.getCountryStateCityData().subscribe(data => {
        this.jsonData = data;
        this.loadCountries();
        resolve();
      });
    });
  }

  loadCountries(): void {
    if (this.jsonData) {
      this.countries = Object.keys(this.jsonData).map(country => ({
        label: country,
        value: country
      }));
    }
  }

  onCountryChange(event: any, index: number): void {
    const selectedCountry = event.value;
    const locationGroup = this.locationInformation.at(index) as FormGroup;

    // Reset state and city when country changes
    locationGroup.patchValue({
      state: '',
      city: ''
    });

    if (selectedCountry && this.jsonData && this.jsonData[selectedCountry]) {
      this.locationOptions[index].states = Object.keys(this.jsonData[selectedCountry]).map(state => ({
        label: state,
        value: state
      }));
      this.locationOptions[index].cities = [];
    } else {
      this.locationOptions[index].states = [];
      this.locationOptions[index].cities = [];
    }
  }

  onStateChange(event: any, index: number): void {
    const selectedState = event.value;
    const locationGroup = this.locationInformation.at(index) as FormGroup;
    const countryName = locationGroup.get('country')?.value;

    // Reset city when state changes
    locationGroup.patchValue({
      city: ''
    });

    if (countryName && selectedState && this.jsonData && this.jsonData[countryName] && this.jsonData[countryName][selectedState]) {
      this.locationOptions[index].cities = this.jsonData[countryName][selectedState].map((city: string) => ({
        label: city,
        value: city
      }));
    } else {
      this.locationOptions[index].cities = [];
    }
  }

  editDescription(): void {
    this.editingDescription.set(true);
    setTimeout(() => {
      this.loadTinyMce();
    }, 100);
  }

  cancelEditDescription(): void {
    this.editingDescription.set(false);
    if (tinymce.activeEditor) {
      tinymce.activeEditor.destroy();
    }
    this.showEditor = false;
  }

  async loadTinyMce(): Promise<void> {
    await this.globalService.loadScript(
      'script',
      `https://cdn.tiny.cloud/1/${this.tinymce_api_key}/tinymce/8.0.2/tinymce.min.js`,
      'text/javascript',
      'examlyck'
    );

    tinymce.remove();
    tinymce.init({
      selector: 'textarea[formControlName="roleDescription"]',
      height: 400,
      menubar: true,
      plugins: [
        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
        'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
      ],
      toolbar: 'undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help',
      content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
      setup: (editor: any) => {
        editor.on('init', () => {
          this.showEditor = true;
          if (this.roleData?.description) {
            editor.setContent(this.roleData.description);
          }
        });
        editor.on('change', () => {
          const content = editor.getContent();
          this.roleDetailsForm.patchValue({ roleDescription: content });
        });
      }
    });
  }

  getPlainTextLength(htmlContent: string): number {
    if (!htmlContent) return 0;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const plainText = tempDiv.textContent || tempDiv.innerText || '';
    return plainText.trim().length;
  }

  checkMandatory(data: any) {
    let success = true;
    const roleDescriptionLength = this.getPlainTextLength(data.roleDescription || '');
    const hasRoleDescription = data.roleDescription && roleDescriptionLength > 0;

    if (data.roleName && data.roleType && hasRoleDescription && roleDescriptionLength >= 250) {
      data.locationInformation.forEach((each: any) => {
        if (!(each?.city?.length && each?.country?.length && each?.state?.length)) {
          success = false;
        }
      });
    } else {
      success = false;
    }
    return success;
  }

  onSave(): void {
    this.clicked = true;

    // Mark all controls as touched
    Object.keys(this.roleDetailsForm.controls).forEach(key => {
      const control = this.roleDetailsForm.get(key);
      if (control) {
        control.markAsTouched();
      }
    });

    this.locationInformation.controls.forEach((locationControl, index) => {
      Object.keys((locationControl as FormGroup).controls).forEach(key => {
        const control = (locationControl as FormGroup).get(key);
        if (control) {
          control.markAsTouched();
        }
      });
    });

    // Security validation for basic fields
    const fieldsToCheck = ['roleName'];
    if (this.sharedService.hasSecurityViolations(this.roleDetailsForm, fieldsToCheck)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid Input',
        detail: 'Invalid input format in some fields.'
      });
      return;
    }

    // Security validation for location fields
    let hasLocationSecurityViolations = false;
    this.locationInformation.controls.forEach((locationControl, index) => {
      const businessUnitValue = (locationControl as FormGroup).get('businessUnit')?.value;
      if (businessUnitValue && this.sharedService.hasSingleInputViolation(businessUnitValue)) {
        hasLocationSecurityViolations = true;
      }
    });

    if (hasLocationSecurityViolations) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid Input',
        detail: 'Invalid input format in some fields.'
      });
      return;
    }

    // Get description from TinyMCE
    let roleDescriptionContent = this.roleDetailsForm.value.roleDescription || '';
    if (tinymce.activeEditor) {
      roleDescriptionContent = tinymce.activeEditor.getContent() || '';
    }
    this.roleDetailsForm.patchValue({ roleDescription: roleDescriptionContent });

    const roleDescriptionLength = this.getPlainTextLength(roleDescriptionContent);
    const hasRoleDescription = roleDescriptionContent && roleDescriptionLength > 0;

    if (hasRoleDescription && roleDescriptionLength < 250) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Failed',
        detail: 'Role description must be at least 250 characters',
      });
      return;
    }

    if (!this.checkMandatory(this.roleDetailsForm.value)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Failed',
        detail: 'Please Fill Mandatory Fields',
      });
      return;
    }

    // Check openings vs max applicants
    const invalidEntries = this.roleDetailsForm.value.locationInformation.filter(
      (loc: any) =>
        loc.openings !== null &&
        loc.maximumApplicants !== null &&
        +loc.openings > +loc.maximumApplicants
    );
    if (invalidEntries.length > 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Openings cannot be greater than Maximum Applicants',
      });
      return;
    }

    this.updateRole();
  }

  saveChanges(): void {
    this.clicked = true;

    if (!this.roleDetailsForm.valid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Failed',
        detail: 'Please fill all mandatory fields',
      });
      return;
    }

    // Check openings vs max applicants
    const invalidEntries = this.roleDetailsForm.value.locationInformation.filter(
      (loc: any) =>
        loc.openings !== null &&
        loc.maximumApplicants !== null &&
        +loc.openings > +loc.maximumApplicants
    );
    if (invalidEntries.length > 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Openings cannot be greater than Maximum Applicants',
      });
      return;
    }

    this.updateRole();
  }

  updateRole() {
    this.saveBtnLoading = true;

    const updateJobRolePayload = {
      name: this.roleDetailsForm.value.roleName,
      role_type_id: this.roleDetailsForm.value.roleType,
      description: (this.roleDetailsForm.value.roleDescription || '')
        .replace(/^[\s\n\r]+/, '')
        .replace(/[\s\n\r]+$/, '')
        .replace(/^\s*(<p>&nbsp;<\/p>|\s)*/, '')
        .trim(),
      work_mode: this.roleDetailsForm.value.workMode,
      locations: this.roleDetailsForm.value.locationInformation.map((each: any) => ({
        country: each.country || '',
        state: each.state || '',
        city: each.city ? (Array.isArray(each.city) ? each.city : [each.city]) : [],
        business_unit: each.businessUnit || '',
        opening: each.openings || 0,
        max_applicant: each.maximumApplicants || 0,
        diversity_male: each.diversityMale || 0,
        diversity_female: each.diversityFemale || 0,
        diversity_other: each.diversityOther || 0,
      })),
      attachment: this.attachmentName
    };

    this.roleService.updateJobRolesFastApi(this.roleId, updateJobRolePayload).subscribe({
      next: (response: any) => {
        this.saveBtnLoading = false;
        if (response.status === 200) {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Role updated successfully",
          });
          this.saveBtnLoading = false;
          this.hasChanges = false;

          // Reload the role details to sync with database
          this.getRoleDetails();
          if (this.editingDescription()) {
            this.cancelEditDescription();
          }
        }
      },
      error: () => {
        this.saveBtnLoading = false;
      }
    });
  }

  updateBreadcrumb(): void {
    this.breadcrumbService.set('@roleDetail', this.roleData?.name || 'Role Details');
  }

  goBack(): void {
    this.router.navigate(["/roles"])
  }

  getDiversityText(location: any): string {
    const male = location.diversity_male || location.diversityMale || 0
    const female = location.diversity_female || location.diversityFemale || 0
    const other = location.diversity_other || location.diversityOther || 0
    return `Male: ${male}, Female: ${female}, Other: ${other}`
  }

  getAttachmentName(url: string): string {
    return url?.split('/').pop() || 'Document';
  }

  downloadAttachment(url: string): void {
    if (url) {
      window.open(url, '_blank');
    }
  }

  onSelectMedia(event: any) {
    this.mediaFile = event.addedFiles;
    this.handleUpload(event);
  }

  onRemoveMedia(event: any) {
    this.mediaFile.splice(this.mediaFile.indexOf(event), 1);
    this.attachmentName = null;
  }

  handleUpload(event: any) {
    const file = event.addedFiles[0];
    if (!file) {
      console.error('No file selected');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'File size should be less than 2MB'
      });
      return;
    }

    const fileName = this.generateFileName(file);
    const renamedFile = new File([file], fileName, { type: file.type });
    const filePath = `temp/${this.companyId}/${fileName}`;

    const payload = {
      file_path: filePath,
      bucket_type: 'public',
      origin: "job_role_creation",
    };

    this.uploadingFile = true;
    this.fileUploadService.getSignedUrlGCS(payload).subscribe({
      next: (response: any) => {
        if (response && response.body.signed_url) {
          this.uploadFileToGCS(response.body.signed_url, renamedFile, filePath, fileName);
        }
      },
      error: (error) => {
        console.error('Error getting signed URL:', error);
        this.uploadingFile = false;
      }
    });
  }

  private generateFileName(file: any): string {
    let fileName = file.name.split(".");
    let fileExtension = fileName.pop();
    const timestamp = new Date().getTime();
    return `${fileName.join().replace(/\s/g, "").replace(/[^\w\s]/gi, '')}_${timestamp}.${fileExtension}`;
  }

  uploadFileToGCS(signedUrl: string, file: any, filePath: string, filename: string) {
    this.fileUploadService.uploadUsingSignedUrlGCS(signedUrl, file).subscribe({
      next: (response: any) => {
        if (response && response.status === "success") {
          this.attachmentName = filename;
          this.uploadingFile = false;
        }
      },
      error: (error) => {
        console.error('Error uploading file to GCS:', error);
        this.uploadingFile = false;
      }
    });
  }
}